import { useEffect, useRef, useState } from 'react';

const getMousePos = (e, container) => {
  if (container) {
    const bounds = container.getBoundingClientRect();
    return {
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
    };
  }
  return { x: e.clientX, y: e.clientY };
};

const Crosshair = ({ color = 'white', containerRef = null }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Declare hooks unconditionally
  const cursorRef = useRef(null);
  const lineHorizontalLeftRef = useRef(null);
  const lineHorizontalRightRef = useRef(null);
  const lineVerticalTopRef = useRef(null);
  const lineVerticalBottomRef = useRef(null);
  const squareRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) return; // Do not execute effect logic on mobile

    document.body.style.overflow = 'hidden';

    const handleMouseMove = (ev) => {
      const mouse = getMousePos(ev, containerRef?.current);
      const x = Math.max(0, Math.min(window.innerWidth, mouse.x));
      const y = Math.max(0, Math.min(window.innerHeight, mouse.y));
      const squareSize = 20;

      if (lineHorizontalLeftRef.current) {
        lineHorizontalLeftRef.current.style.width = `${Math.max(0, x - squareSize / 2)}px`;
        lineHorizontalLeftRef.current.style.top = `${y}px`;
      }
      if (lineHorizontalRightRef.current) {
        lineHorizontalRightRef.current.style.left = `${x + squareSize / 2}px`;
        lineHorizontalRightRef.current.style.width = `${Math.max(0, window.innerWidth - (x + squareSize / 2))}px`;
        lineHorizontalRightRef.current.style.top = `${y}px`;
      }
      if (lineVerticalTopRef.current) {
        lineVerticalTopRef.current.style.height = `${Math.max(0, y - squareSize / 2)}px`;
        lineVerticalTopRef.current.style.left = `${x}px`;
      }
      if (lineVerticalBottomRef.current) {
        lineVerticalBottomRef.current.style.top = `${y + squareSize / 2}px`;
        lineVerticalBottomRef.current.style.height = `${Math.max(0, window.innerHeight - (y + squareSize / 2))}px`;
        lineVerticalBottomRef.current.style.left = `${x}px`;
      }
      if (squareRef.current) {
        squareRef.current.style.left = `${x - squareSize / 2}px`;
        squareRef.current.style.top = `${y - squareSize / 2}px`;
      }
    };

    const target = containerRef?.current || window;
    target.addEventListener('mousemove', handleMouseMove);

    return () => {
      target.removeEventListener('mousemove', handleMouseMove);
      document.body.style.overflow = ''; // Restore scrolling when unmounting
    };
  }, [containerRef, isMobile]);

  // Render nothing if on mobile
  if (isMobile) return null;

  return (
    <div
      ref={cursorRef}
      className="cursor"
      style={{
        position: containerRef ? 'absolute' : 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10000,
        overflow: 'hidden',
      }}
    >
      <div ref={lineHorizontalLeftRef} style={{ position: 'absolute', height: '1px', background: color }}></div>
      <div ref={lineHorizontalRightRef} style={{ position: 'absolute', height: '1px', background: color }}></div>
      <div ref={lineVerticalTopRef} style={{ position: 'absolute', width: '1px', background: color }}></div>
      <div ref={lineVerticalBottomRef} style={{ position: 'absolute', width: '1px', background: color }}></div>
      <div
        ref={squareRef}
        style={{
          position: 'absolute',
          width: '20px',
          height: '20px',
          background: 'transparent',
          border: `1px solid ${color}`,
          pointerEvents: 'none',
        }}
      ></div>
    </div>
  );
};

export default Crosshair;
